import React, { useEffect, useMemo, useRef, useState } from "react"
import { Form, Modal } from 'react-bootstrap'
import { useHistory } from "react-router"
import { useRecoilValue } from "recoil"
import { FormProduct } from "."
import { getInventoryInPharmacy, getPharmacyInfoLocal } from "../../api"
import { getProductStats } from "../../api"
import usePharmacy from "../../hooks/pharmacy"
import { InventoryModel, InventoryModelWithProductStat } from "../../models"
import { PharmacyDetailModel } from "../../models"
import { accountDataState } from "../../recoil/account"
import { debounce, formatPrice } from "../../utils"
import { useAsync } from "../../utils/hooks"
import Loading from "../shared/Loading"
import { useDebounce } from "use-debounce"
import ReactPaginate from 'react-paginate';

type IInStock = 'all' | 'stocking' | 'outOfStock'
type IDropshipping = 'all' | 'available' | 'dropShipping'
type IKeySort = "ASC" | "DESC" | "NONE"
type IFieldSort = "mProductName" | "mPkgName" | "mStockAmount" | "mPrice" | "mCreated" | "mModified" | ""

interface IState {
    skip: number,
    limit: number,
    listProduct: Array<InventoryModelWithProductStat>,
    inStock: IInStock,
    dropShipping: IDropshipping;
    total?: number,
    sort: {
        field: IFieldSort
        status: IKeySort
    },
    search: string,
    fetched: boolean
}

const initState: IState = {
    skip: 0,
    limit: 20,
    listProduct: [],
    inStock: 'all',
    dropShipping: 'all',
    total: 0,
    sort: {
        field: "mModified",
        status: "DESC"
    },
    search: '',
    fetched: false,
}

const InventoryOfPharmacy = React.memo(() => {
    const history = useHistory()

    const { getCurrentDetailPharmacy } = usePharmacy()
    const getCurrentDetailPharmacyAsync = useAsync<PharmacyDetailModel>(getCurrentDetailPharmacy)
    const getInventoryInPharmacyAsync = useAsync(getInventoryInPharmacy)

    const accountInfo = useRecoilValue(accountDataState)
    const adminId = useMemo(() => {
        if (accountInfo.loginSession.userLocalType === 'ADMIN') return accountInfo.doctor.mId
    }, [accountInfo])

    const { pharmacyDetail, pharmacyId } = useMemo(() => {
        return {
            pharmacyDetail: getCurrentDetailPharmacyAsync.value, pharmacyId: getCurrentDetailPharmacyAsync.value?.pharmacy.mId,
        }
    }, [getCurrentDetailPharmacyAsync.value])

    const [state, setState] = useState<IState>(initState)
    const inputSearch: any = useRef()
    const tableProduct: any = useRef()
    const page: any = useRef()
    const [selectedProductId, setSelectedProductId] = useState<string>("")
    const [selectedInvItemId, setSelectedInvItemId] = useState<number>(0)
    const [showModal, setShowModal] = useState(false);
    const [searchTermDebounce, time] = useDebounce<string>(state.search, 400)

    const backToTop = () => {
        if (!tableProduct.current?.scrollTop) return
        tableProduct.current.scrollTop = 0
    }

    useEffect(() => {
        const pharmacyInfo = getPharmacyInfoLocal()
        if (pharmacyInfo) {
            getCurrentDetailPharmacyAsync.execute()
        } else {
            console.error("Not found pharmacy info");
        }
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            if (!state.fetched) {
                return
            }

            try {
                if (!pharmacyId) {
                    return
                }

                let packageIds: string[] = state.listProduct.map(item => item.mPackageId)

                let data = await getProductStats(pharmacyId, {
                    where: {
                        and: [
                            {
                                mPackageId: {
                                    inq: packageIds
                                }
                            },
                            {
                                mAdministrativeAreaLevel_0: pharmacyDetail.pharmacyAddress.mAdministrativeAreaLevel0
                            }
                        ]
                    }
                }, adminId)

                for (let p of state.listProduct) {
                    let found = data.find(item => item.mPackageId == p.mPackageId)

                    if (found) {
                        p.productStat = found

                        // in case cronjob not update realtime
                        if (p.mPrice < found.mMinPrice) {
                            found.mMinPrice = p.mPrice
                        }
                    }
                }
                setState(prev => ({ ...prev, listProduct: state.listProduct }))
            } catch (err) {
                console.error("cannot fetch data: ", err);
            }
        }

        fetchData()
    }, [state?.fetched])

    useEffect(() => {
        // Reset skip
        if (!pharmacyId) return
        backToTop()
        const { field, status } = state.sort
        const order = status !== "NONE" && `${field} ${status}`
        const filter: any = {
            skip: initState.skip,
            limit: state.limit,
            where: {
                ...getQuerySearch(searchTermDebounce),
                ...getQueryByStock(state.inStock),
            },
        }
        const dropShipping = state.dropShipping
        if (order) {
            Object.assign(filter, { order })
        }
        updateToFetchingState()
        getInventoryInPharmacyAsync.execute({
            pharmacyId,
            adminId: adminId,
            filter,
            dropShipping
        }).then(res => {     
            setState(prev => ({
                ...prev,
                skip: initState.skip,
                listProduct: res.listInventory,
                total: res.total,
                fetched: true
            }))
        })
    }, [state?.inStock, state?.dropShipping, state?.limit, searchTermDebounce])

    useEffect(() => {
        fetchListWithCurrentState()
    }, [pharmacyId, state?.sort, state?.skip])

    useEffect(() => {
        setShowModal(selectedProductId ? true : false)
    }, [selectedProductId])

    const updateToFetchingState = () => {
        setState(prev => ({ ...prev, fetched: false }))
    }

    const getQuerySearch = (value: string) => {
        if (value !== '') {

            value = value.replace(/\s+/g, ' ').trim()

            return {
                mProductName: {
                    like: `%${value}%`
                }
            }
        }
        return {}
    }

    const getQueryByStock = (value: IInStock) => {
        switch (value) {
            case 'all':
                return {}
            case 'stocking':
                return { mInStock: true }
            case 'outOfStock':
                return { mInStock: false }
        }
    }

    const fetchListWithCurrentState = async () => {
        if (!pharmacyId) return
        backToTop()
        const { field, status } = state.sort
        const order = status !== "NONE" && `${field} ${status}`
        const filter: any = {
            skip: state.skip,
            limit: state.limit,
            where: {
                ...getQuerySearch(searchTermDebounce),
                ...getQueryByStock(state.inStock)
            }
        }
        const dropShipping = state.dropShipping
        if (order) {
            Object.assign(filter, { order })
        }
        updateToFetchingState()
        return await getInventoryInPharmacyAsync.execute({
            pharmacyId,
            adminId: adminId,
            filter,
            dropShipping
        }).then(res => {
            setState(prev => ({
                ...prev,
                listProduct: res.listInventory,
                total: res.total,
                fetched: true,
            }))

            return res
        })
    }

    const handleUpdateSuccess = (invItems: any[]) => {
        fetchListWithCurrentState()
        setSelectedProductId("")
    }

    const handleCancelUpdate = () => {
        setSelectedProductId("")
    }

    const handleDeleteProduct = async (productId: string) => {
        setSelectedProductId("")
        const res = await fetchListWithCurrentState()
        if (!res?.listInventory?.length) {
            if (state.skip - state.limit >= 1) {
                setState(prev => ({ ...prev, skip: prev.skip - prev.limit }))
            }
        }
    }

    const UpdateProductModal = () => {
        return (
            <Modal show={showModal}
                onHide={() => setSelectedProductId("")}
                dialogClassName="product-update fullscreen"
            >
                <Modal.Body>
                    {
                        selectedProductId ?
                            <FormProduct
                                productId={selectedProductId}
                                isModal={true}
                                handleCancelUpdate={handleCancelUpdate}
                                handleUpdateSuccess={handleUpdateSuccess}
                                handleDeleteProduct={handleDeleteProduct}
                            ></FormProduct>
                            : <></>
                    }

                </Modal.Body>
            </Modal>
        );
    }

    const inactiveProductIds = useMemo(() => {
        const ids = new Set<string>()
        state.listProduct.forEach(p => {
            if (!p.mIsActive) {
                ids.add(p.mProductId)
            }
        })
        return ids
    }, [state.listProduct])

    return <>
        <div id="main" style={{
            width: "100%",
            // paddingTop: '65px',
            height: '100vh',
            minHeight: '-webkit-fill-available',
        }}
        >

            {
                (getCurrentDetailPharmacyAsync.status == "loading") ?
                    <div className="wrap-loading">
                        <Loading />
                    </div> :
                    <>
                        <div className="container">
                            <UpdateProductModal></UpdateProductModal>

                            <p className="mt-3 mb-3">
                                <a onClick={() => history.push('search-product')} className="btn btn-block btn-border" title="THÊM SẢN PHẨM MỚI">
                                    <img src="./images/add_circle.svg" title="Add" className="img-fluid" width={24} />
                                    THÊM SẢN PHẨM MỚI
                                </a>
                            </p>

                            <div className="row">
                                <div className="spacing" />
                            </div>

                            <div className="form-search my-3">
                                <form id="search">
                                    <input
                                        ref={inputSearch}
                                        onKeyPress={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                                        onChange={(e) => {
                                            setState(prev => ({ ...prev, search: e.target.value }))

                                        }} type="text" name="search" placeholder="Tìm trong kho hàng nhà thuốc" className="input-search" value={state.search} />
                                    <a
                                        id="btn-search-clear"
                                        onClick={() => {
                                            inputSearch.current.value = ''
                                            setState(prev => ({ ...prev, search: '' }))

                                        }}
                                    >
                                        <img src="./images/cancel.svg" title="Xóa" className="img-fluid" width={24} alt='' />
                                    </a>
                                    <button type="button" className="btn-search">
                                        <img src="./images/search.svg" title="Tìm kiếm" className="img-fluid" width={24} />
                                    </button>
                                </form>
                            </div>
                            {getCurrentDetailPharmacyAsync.value?.pharmacy?.mType !== "DEFAULT_PHARMACY" && 
                            <div className="d-flex">
                                <div className="w-50 mr-2 d-flex align-items-center">
                                    <Form.Label className='m-0 mr-1 mb-2'><b>Tình trạng</b></Form.Label>
                                    <Form.Control onChange={(e) => {
                                        const value = e.target.value as IInStock
                                        setState(prev => ({ ...prev, inStock: value }))
                                    }} name='filter' as="select" value={state.inStock} custom>
                                        <option value='all'>Tất cả</option>
                                        <option value='stocking'>Còn hàng</option>
                                        <option value='outOfStock'>Hết hàng</option>
                                    </Form.Control>
                                </div>
                                <div className="w-50 d-flex align-items-center">
                                    <Form.Label className='m-0 mr-1 mb-2'><b>Sắp xếp</b></Form.Label>
                                    <Form.Control onChange={(e) => {
                                        const value = e.target.value as IFieldSort
                                        setState(prev => ({
                                            ...prev,
                                            sort: {
                                                field: value,
                                                status: "DESC"
                                            }
                                        }))
                                    }
                                    } name='sort' as="select" value={state.sort.field} custom>
                                        <option value='mCreated'>Ngày tạo</option>
                                        <option value='mModified'>Ngày cập nhật</option>
                                        <option value='mProductName'>Tên</option>
                                    </Form.Control>
                                </div>
                            </div>}
                            {getCurrentDetailPharmacyAsync.value?.pharmacy?.mType === "DEFAULT_PHARMACY" && 
                            <div className="d-flex">
                                <div className="w-30 mr-2 align-items-center">
                                    <Form.Label className='m-0 mr-1 mb-2'><b>Tình trạng</b></Form.Label>
                                    <Form.Control onChange={(e) => {
                                        const value = e.target.value as IInStock
                                        setState(prev => ({ ...prev, inStock: value }))
                                    }} name='filter' as="select" value={state.inStock} custom>
                                        <option value='all'>Tất cả</option>
                                        <option value='stocking'>Còn hàng</option>
                                        <option value='outOfStock'>Hết hàng</option>
                                    </Form.Control>
                                </div>
                                <div className="w-30 mr-2 align-items-center">
                                    <Form.Label className='m-0 mr-1 mb-2'><b>Sắp xếp</b></Form.Label>
                                    <Form.Control onChange={(e) => {
                                        const value = e.target.value as IFieldSort
                                        setState(prev => ({
                                            ...prev,
                                            sort: {
                                                field: value,
                                                status: "DESC"
                                            }
                                        }))
                                    }
                                    } name='sort' as="select" value={state.sort.field} custom>
                                        <option value='mCreated'>Ngày tạo</option>
                                        <option value='mModified'>Ngày cập nhật</option>
                                        <option value='mProductName'>Tên</option>
                                    </Form.Control>
                                </div>
                                <div className="w-40 align-items-center">
                                    <Form.Label className='m-0 mr-1 mb-2'><b>Loại sản phẩm</b></Form.Label>
                                    <Form.Control onChange={(e) => {
                                        const value = e.target.value as IDropshipping
                                        setState(prev => ({ ...prev, dropShipping: value }))
                                    }} name='filter' as="select" value={state.dropShipping} custom>
                                        <option value='all'>Tất cả</option>
                                        <option value='available'>Có sẵn</option>
                                        <option value='dropShipping'>Chạy hàng</option>
                                    </Form.Control>
                                </div>
                            </div>}

                        </div>
                        {
                            state.listProduct.length === 0 &&
                            <div className="d-flex py-3 justify-content-center align-items-center">
                                <p className="m-0">Không tìm thấy sản phẩm nào</p>
                            </div>
                        }

                        <div className="row mt-2"></div>
                        
                        {
                            state.listProduct.length !== 0 &&
                            <>
                                <div ref={tableProduct} className="table-products" style={{ position: "relative", maxHeight: 'unset' }}>
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Tên</th>
                                                <th style={{ width: 40 }}>ĐV</th>
                                                <th style={{ width: 80 }}>TT</th>
                                                <th style={{ width: 60 }}>ĐƠN GIÁ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                state.listProduct?.map((product: InventoryModelWithProductStat) => {
                                                    return <tr key={product.mId} onClick={() => {
                                                        setSelectedProductId(product.mProductId)
                                                        setSelectedInvItemId(product.mId)
                                                    }} className={product.mId == selectedInvItemId ? "table-active" : ""}>
                                                        <td className="custom-text-overflow">
                                                            {product.mProductName}
                                                            {product.mIsDropShipping &&  
                                                            <div style={{ alignItems: 'center', display: 'flex', marginTop: 4 }}> 
                                                                <img src="./images/dropshipping.svg" title="Dropshipping" className="img-fluid" width={14} /> 
                                                                <span className="dropshipping-names" style={{
                                                                    color: '#F58334',
                                                                    marginLeft: 5,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {product.dropshippingPhars || ''}
                                                                </span>
                                                            </div>}
                                                            {inactiveProductIds.has(product.mProductId) &&  
                                                            <div style={{ marginTop: 4 }}> 
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    backgroundColor: '#FFF3E0',
                                                                    color: '#E65100',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid #FFB74D'
                                                                }}>
                                                                    Ngừng kinh doanh
                                                                </span>
                                                            </div>}
                                                            {product.mIsRetailInvoice &&  
                                                            <div style={{ marginTop: 4 }}> 
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    backgroundColor: '#E8F5E9',
                                                                    color: '#2E7D32',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid #81C784'
                                                                }}>
                                                                    Xuất hóa đơn bán lẻ
                                                                </span>
                                                            </div>}
                                                        </td>
                                                        <td style={{ width: 40 }}>{product.mPkgName}</td>
                                                        <td style={{ width: 80 }}>
                                                            {
                                                                product.mInStock ?
                                                                    <b style={{ borderRadius: 25, color: '#36cd7a', border: '1px solid #36cd7a', padding: '3px 4px' }}>Còn hàng</b> :
                                                                    <b style={{ borderRadius: 25, color: '#F35353', border: '1px solid #F35353', padding: '3px 4px' }}>Hết hàng</b>
                                                            }
                                                        </td>
                                                        <td className={`${state.sort.field === "mPrice" && 'active-column'} price`} style={{ width: 60 }}>
                                                            {formatPrice(product.mPrice)} <br />
                                                            <span className="txt-orange">{formatPrice(product.productStat?.mMinPrice)}</span>
                                                        </td>
                                                    </tr>
                                                })
                                            }

                                        </tbody>
                                    </table>
                                    {
                                        getInventoryInPharmacyAsync.status == "loading" &&
                                        <div className="wrap-loading">
                                            <Loading />
                                        </div>
                                    }
                                </div>
                                <div className="d-flex align-items-center justify-content-start mt-3 px-3">
                                    <div className="pagination mt-3">
                                        {
                                            state.total &&
                                            <ReactPaginate
                                                pageClassName='page-item'
                                                breakLabel="..."
                                                onPageChange={(page: any) => {
                                                    console.log('page: ', page);
                                                    setState(prev => ({ ...prev, skip: state.limit * page.selected }))
                                                }}
                                                pageCount={Math.ceil(state.total / state.limit)}
                                                pageRangeDisplayed={1}
                                                marginPagesDisplayed={1}
                                                previousLabel={<b className="pagination-navigator">{'<'}</b>}
                                                nextLabel={<b className="pagination-navigator"> {'>'} </b>}
                                                forcePage={(state.skip) / state.limit}
                                            />
                                        }
                                    </div>
                                    <Form.Control
                                        className="select-page-limit"
                                        onChange={(e) => {
                                            const value = e.target.value
                                            setState(prev => ({
                                                ...prev,
                                                limit: +value,
                                            }))
                                        }
                                        }
                                        name='limit'
                                        as="select"
                                        value={state.limit}
                                        custom
                                    >
                                        <option value='10'>10</option>
                                        <option value='20'>20</option>
                                        <option value='50'>50</option>
                                        <option value='100'>100</option>
                                    </Form.Control>
                                </div>
                                <div className="d-flex align-items-center justify-content-end mb-4">
                                    <div className="d-flex align-items-center justify-content-end mr-3"
                                        style={{ width: '60%' }}
                                    >
                                        <b>Tổng số {state.total} sản phẩm</b>
                                    </div>
                                    <div className="d-flex align-items-center mr-3 justify-content-end"
                                        style={{ width: '40%' }}>
                                        <Form.Control type="number" className="mr-2" placeholder="Đến trang"
                                            ref={page}
                                            min={1}
                                            style={{ width: '68%', height: '30px' }} />
                                        <Form.Control
                                            as="button"
                                            onClick={() => {
                                                if (page.current.value > 0 && state.total && page.current.value <= Math.ceil(state.total / state.limit)) {
                                                    setState(prev => ({ ...prev, skip: state.limit * (page.current.value - 1) }))
                                                }
                                            }}
                                            style={{ width: '40px', height: '30px', color: '#fff', backgroundColor: '#36cd7a', border: 'none' }}
                                        >Đi</Form.Control>
                                    </div>
                                </div>
                            </>
                        }
                    </>
            }
        </div>
    </>
})

export default InventoryOfPharmacy